import * as Dialog from "@radix-ui/react-dialog"
import { ChevronDownIcon, Cross2Icon, PlusIcon, TrashIcon } from "@radix-ui/react-icons"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import { AppShell } from "@/components/app-shell"
import { ConfirmDialog } from "@/components/confirm-dialog"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ModelCombobox } from "@/components/ui/model-combobox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { dialogOpenAutoFocusToTitle, dialogTitleClassName } from "@/lib/dialog-title-focus"
import { emitToast } from "@/lib/sonner"
import { cn } from "@/lib/utils"

const ACTIVE_METHODOLOGY_LOCKED_TOAST = {
  type: "warning",
  title: "Active methodology unchanged",
  description: "Methodology changes are disabled for now. We will add controls for this later."
}

function warnActiveMethodologyLocked(description = ACTIVE_METHODOLOGY_LOCKED_TOAST.description) {
  emitToast({ ...ACTIVE_METHODOLOGY_LOCKED_TOAST, description }, "active-methodology-locked")
}

function formatDate(value) {
  if (!value) return "-"

  return new Date(value).toLocaleString()
}

function joinErrors(errors, key) {
  const value = errors[key]
  if (!value) return null

  return Array.isArray(value) ? value.join(", ") : value
}

function methodologyLabel(methodology) {
  return `${methodology.label} (${methodology.key})`
}

function firstOption(options) {
  return options[0] || ""
}

function preferredOption(options, preferredValue) {
  if (!preferredValue) return firstOption(options)

  return options.includes(preferredValue) ? preferredValue : preferredValue
}

function buildMethodologyKey(label, retrievalEngine) {
  const base = `${retrievalEngine}_${label}`.toLowerCase()
  return base.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
}

function generatedBenchmarkName(value) {
  return /^Benchmark\s+\d+$/i.test(value.toString().trim())
}

function ModelField({ id, label, onChange, options, placeholder, value }) {
  return (
    <div>
      <label className="text-sm font-medium text-[var(--color-charcoal-blue)]" htmlFor={id}>{label}</label>
      <ModelCombobox
        id={id}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        value={value}
      />
    </div>
  )
}

function MethodologyDialog({
  chatModelOptions,
  embeddingModelOptions,
  onOpenChange,
  open,
  organizationBasePath
}) {
  const [keyEdited, setKeyEdited] = useState(false)
  const titleId = "create-methodology-title"
  const form = useForm({
    label: "",
    key: "",
    retrieval_engine: "pgvector",
    answer_model: firstOption(chatModelOptions),
    question_generation_model: firstOption(chatModelOptions),
    judge_model: firstOption(chatModelOptions),
    embedding_model: firstOption(embeddingModelOptions),
    enabled: true
  })

  useEffect(() => {
    if (keyEdited) return

    form.setData("key", buildMethodologyKey(form.data.label, form.data.retrieval_engine))
  }, [form.data.label, form.data.retrieval_engine, form, keyEdited])

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[rgb(12_48_60_/_0.42)] backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[min(94vw,42rem)] -translate-x-1/2 -translate-y-1/2 rounded-[1.8rem] bg-white p-6 shadow-[var(--shadow-soft)] ring-1 ring-[var(--color-border)]"
          onOpenAutoFocus={dialogOpenAutoFocusToTitle(titleId)}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title
                className={cn("text-2xl font-semibold text-[var(--color-charcoal-blue)]", dialogTitleClassName)}
                id={titleId}
                tabIndex={-1}
              >
                Create methodology
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-sm text-[var(--color-text-muted)]">
                Add a new retrieval profile with its own answer, judge, question, and embedding models.
              </Dialog.Description>
            </div>

            <Dialog.Close asChild>
              <Button size="icon" type="button" variant="ghost">
                <Cross2Icon className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <form
            className="mt-6 grid gap-4 md:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault()
              form.transform((data) => ({ methodology: data }))
              form.post(`${organizationBasePath}/settings/methodologies`, {
                onSuccess: () => {
                  form.reset()
                  form.setData("retrieval_engine", "pgvector")
                  form.setData("answer_model", firstOption(chatModelOptions))
                  form.setData("question_generation_model", firstOption(chatModelOptions))
                  form.setData("judge_model", firstOption(chatModelOptions))
                  form.setData("embedding_model", firstOption(embeddingModelOptions))
                  form.setData("enabled", true)
                  setKeyEdited(false)
                  onOpenChange(false)
                }
              })
            }}
          >
            <div>
              <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Label</label>
              <input
                className="input-field mt-2"
                onChange={(event) => form.setData("label", event.target.value)}
                value={form.data.label}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Key</label>
              <input
                className="input-field mt-2"
                onChange={(event) => {
                  setKeyEdited(true)
                  form.setData("key", event.target.value)
                }}
                value={form.data.key}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Retrieval engine</label>
              <Select
                onValueChange={(value) => form.setData("retrieval_engine", value)}
                value={form.data.retrieval_engine}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select retrieval engine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pgvector">pgvector</SelectItem>
                  <SelectItem value="qmd">QMD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 pt-8">
              <input
                checked={form.data.enabled}
                id="methodology-enabled"
                onChange={(event) => form.setData("enabled", event.target.checked)}
                type="checkbox"
              />
              <label className="text-sm font-medium text-[var(--color-charcoal-blue)]" htmlFor="methodology-enabled">Enabled immediately</label>
            </div>

            <ModelField
              id="new-methodology-answer-model"
              label="Answer model"
              onChange={(value) => form.setData("answer_model", value)}
              options={chatModelOptions}
              placeholder="Choose an answer model"
              value={form.data.answer_model}
            />

            <ModelField
              id="new-methodology-question-model"
              label="Question generation model"
              onChange={(value) => form.setData("question_generation_model", value)}
              options={chatModelOptions}
              placeholder="Choose a question model"
              value={form.data.question_generation_model}
            />

            <ModelField
              id="new-methodology-judge-model"
              label="Judge model"
              onChange={(value) => form.setData("judge_model", value)}
              options={chatModelOptions}
              placeholder="Choose a judge model"
              value={form.data.judge_model}
            />

            <ModelField
              id="new-methodology-embedding-model"
              label="Embedding model"
              onChange={(value) => form.setData("embedding_model", value)}
              options={embeddingModelOptions}
              placeholder="Choose an embedding model"
              value={form.data.embedding_model}
            />

            <div className="md:col-span-2 flex justify-end gap-3">
              <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">Cancel</Button>
              <Button disabled={form.processing} type="submit">Create profile</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function MethodologyEditor({ activeMethodologyId, methodology, chatModelOptions, embeddingModelOptions, organizationBasePath }) {
  const [archiveOpen, setArchiveOpen] = useState(false)
  const archiveForm = useForm({})
  const active = methodology.id === activeMethodologyId
  const form = useForm({
    answer_model: methodology.answer_model || "",
    question_generation_model: methodology.question_generation_model || "",
    judge_model: methodology.judge_model || "",
    embedding_model: methodology.embedding_model || ""
  })
  const setModelValue = (field, value) => {
    if (active) {
      warnActiveMethodologyLocked("Model changes are disabled for the active methodology for now.")
      return
    }

    form.setData(field, value)
  }
  const requestArchive = () => {
    if (active) {
      warnActiveMethodologyLocked("Archive another profile first or wait for methodology controls to be added.")
      return
    }

    setArchiveOpen(true)
  }

  return (
    <>
      <article className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-4 shadow-[var(--shadow-soft)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">{methodology.label}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.12em] text-[var(--color-text-faint)]">{methodology.key}</p>
            <p className="mt-2 text-xs text-[var(--color-text-muted)]">Retrieval engine: {methodology.retrieval_engine}</p>
          </div>

          <Button onClick={requestArchive} type="button" variant="ghost">Archive</Button>
        </div>

        <form
          className="mt-4 grid gap-3 lg:grid-cols-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (active) {
              warnActiveMethodologyLocked("Model changes are disabled for the active methodology for now.")
              return
            }

            form.transform((data) => ({ methodology: data }))
            form.patch(`${organizationBasePath}/settings/methodologies/${methodology.id}`, { preserveScroll: true })
          }}
        >
          <ModelField
            id={`methodology-${methodology.id}-answer`}
            label="Answer model"
            onChange={(value) => setModelValue("answer_model", value)}
            options={chatModelOptions}
            placeholder="Choose an answer model"
            value={form.data.answer_model}
          />

          <ModelField
            id={`methodology-${methodology.id}-question`}
            label="Question generation model"
            onChange={(value) => setModelValue("question_generation_model", value)}
            options={chatModelOptions}
            placeholder="Choose a question model"
            value={form.data.question_generation_model}
          />

          <ModelField
            id={`methodology-${methodology.id}-judge`}
            label="Judge model"
            onChange={(value) => setModelValue("judge_model", value)}
            options={chatModelOptions}
            placeholder="Choose a judge model"
            value={form.data.judge_model}
          />

          <ModelField
            id={`methodology-${methodology.id}-embedding`}
            label="Embedding model"
            onChange={(value) => setModelValue("embedding_model", value)}
            options={embeddingModelOptions}
            placeholder="Choose an embedding model"
            value={form.data.embedding_model}
          />

          <div className="flex items-end lg:col-span-4">
            <Button disabled={form.processing} type="submit" variant="outline">Save profile models</Button>
          </div>
        </form>
      </article>

      <ConfirmDialog
        confirmLabel="Archive profile"
        description="The profile will be removed from active methodology choices and evaluation runs, but can be restored later."
        onConfirm={() => archiveForm.delete(`${organizationBasePath}/settings/methodologies/${methodology.id}`, { preserveScroll: true })}
        onOpenChange={setArchiveOpen}
        open={archiveOpen}
        processing={archiveForm.processing}
        title={`Archive ${methodology.label}?`}
      />
    </>
  )
}

function MethodologyProfilesSection({
  activeMethodologyId,
  archivedMethodologies,
  chatModelOptions,
  embeddingModelOptions,
  methodologies,
  onCreate,
  organizationBasePath
}) {
  const [selectedId, setSelectedId] = useState(methodologies[0]?.id || "")
  const [archivedOpen, setArchivedOpen] = useState(false)
  const selectedMethodology = methodologies.find((methodology) => methodology.id === selectedId) || methodologies[0]

  useEffect(() => {
    if (methodologies.some((methodology) => methodology.id === selectedId)) return

    setSelectedId(methodologies[0]?.id || "")
  }, [methodologies, selectedId])

  return (
    <section className="rounded-[1.75rem] bg-white/82 p-6 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[var(--color-charcoal-blue)]">Methodology profiles</h2>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            Select one profile to edit its chat and embedding models.
          </p>
        </div>

        <Button onClick={onCreate} type="button">
          <PlusIcon className="mr-2 h-4 w-4" />
          New profile
        </Button>
      </div>

      {methodologies.length > 0 ? (
        <>
          <div className="mt-4 flex gap-2 overflow-x-auto rounded-lg bg-[var(--color-panel)] p-1">
            {methodologies.map((methodology) => {
              const active = methodology.id === selectedMethodology?.id

              return (
                <button
                  className={cn(
                    "shrink-0 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors",
                    active
                      ? "bg-white text-[var(--color-charcoal-blue)] shadow-[var(--shadow-soft)]"
                      : "text-[var(--color-text-muted)] hover:bg-white/70 hover:text-[var(--color-charcoal-blue)]"
                  )}
                  key={methodology.id}
                  onClick={() => setSelectedId(methodology.id)}
                  type="button"
                >
                  <span className="block max-w-52 truncate">{methodology.label}</span>
                  <span className="mt-0.5 block text-[0.68rem] uppercase tracking-[0.12em] opacity-70">
                    {methodology.retrieval_engine}
                  </span>
                </button>
              )
            })}
          </div>

          {selectedMethodology ? (
            <div className="mt-4">
              <MethodologyEditor
                activeMethodologyId={activeMethodologyId}
                chatModelOptions={chatModelOptions}
                embeddingModelOptions={embeddingModelOptions}
                key={selectedMethodology.id}
                methodology={selectedMethodology}
                organizationBasePath={organizationBasePath}
              />
            </div>
          ) : null}
        </>
      ) : (
        <p className="mt-4 rounded-lg border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-text-muted)]">
          No active methodology profiles.
        </p>
      )}

      {archivedMethodologies.length > 0 ? (
        <Collapsible className="mt-4" onOpenChange={setArchivedOpen} open={archivedOpen}>
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost">
              Archived profiles ({archivedMethodologies.length})
              <ChevronDownIcon className={cn("ml-2 h-4 w-4 transition-transform", archivedOpen && "rotate-180")} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {archivedMethodologies.map((methodology) => (
                <ArchivedMethodologyCard key={methodology.id} methodology={methodology} organizationBasePath={organizationBasePath} />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </section>
  )
}

function ArchivedMethodologyCard({ methodology, organizationBasePath }) {
  const restoreForm = useForm({})

  return (
    <article className="rounded-[0.5rem] border border-dashed border-[var(--color-border)] bg-white/60 p-4 shadow-[var(--shadow-soft)]">
      <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">{methodology.label}</p>
      <p className="mt-1 text-xs uppercase tracking-[0.12em] text-[var(--color-text-faint)]">{methodology.key}</p>
      <p className="mt-2 text-xs text-[var(--color-text-muted)]">Retrieval engine: {methodology.retrieval_engine}</p>
      <p className="mt-3 text-xs text-[var(--color-text-muted)]">Answer: {methodology.answer_model}</p>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">Judge: {methodology.judge_model}</p>
      <p className="mt-1 text-xs text-[var(--color-text-muted)]">Embedding: {methodology.embedding_model}</p>

      <Button
        className="mt-4"
        disabled={restoreForm.processing}
        onClick={() => restoreForm.patch(`${organizationBasePath}/settings/methodologies/${methodology.id}/restore`, { preserveScroll: true })}
        type="button"
        variant="outline"
      >
        Restore profile
      </Button>
    </article>
  )
}

export default function AssistantSettingsPage({
  archived_methodologies = [],
  chat_model_options = [],
  default_benchmark_dataset_name = "Benchmark 1",
  default_benchmark_generation_instructions = "Create questions from the pdfs and store the pdf name and page reference number with the expected answer (directly from the document).",
  default_benchmark_generator_model = "gemini-pro-latest",
  default_eval_run_model = "openai/gpt-5.4-mini",
  embedding_model_options = [],
  errors = {},
  eval_datasets = [],
  eval_runs = [],
  form,
  knowledge_documents = [],
  methodologies = []
}) {
  const { organization } = usePage().props
  const organizationBasePath = organization?.slug ? `/orgs/${organization.slug}` : ""
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState(null)
  const fallbackMethodology = methodologies[0]?.id || ""
  const lockedActiveMethodologyId = form.active_methodology_id || fallbackMethodology

  const settingsForm = useForm({
    chat_access_mode: form.chat_access_mode || "authenticated_chat",
    active_methodology_id: lockedActiveMethodologyId
  })

  const uploadForm = useForm({ files: [], urls: "" })
  const documentDeleteForm = useForm({})
  const previousDefaultBenchmarkName = useRef(default_benchmark_dataset_name)

  const datasetForm = useForm({
    name: default_benchmark_dataset_name,
    description: default_benchmark_generation_instructions,
    generator_model: preferredOption(chat_model_options, default_benchmark_generator_model),
    case_limit: 20
  })

  useEffect(() => {
    const currentName = datasetForm.data.name.toString().trim()
    if (!currentName || currentName === previousDefaultBenchmarkName.current || generatedBenchmarkName(currentName)) {
      datasetForm.setData("name", default_benchmark_dataset_name)
    }

    previousDefaultBenchmarkName.current = default_benchmark_dataset_name
  }, [default_benchmark_dataset_name])

  const runForm = useForm({
    eval_dataset_id: eval_datasets[0]?.id || "",
    judge_model: preferredOption(chat_model_options, default_eval_run_model),
    question_generation_model: preferredOption(chat_model_options, default_eval_run_model),
    methodology_ids: settingsForm.data.active_methodology_id ? [settingsForm.data.active_methodology_id] : []
  })

  const toggleMethodologySelection = (methodologyId, checked) => {
    const current = runForm.data.methodology_ids || []
    const next = checked
      ? Array.from(new Set([ ...current, methodologyId ]))
      : current.filter((value) => value !== methodologyId)

    runForm.setData("methodology_ids", next)
  }
  const setActiveMethodology = (methodologyId) => {
    if (methodologyId !== lockedActiveMethodologyId) {
      warnActiveMethodologyLocked()
    }

    settingsForm.setData("active_methodology_id", lockedActiveMethodologyId)
  }

  return (
    <>
      <Head title="Assistant settings" />

      <AppShell
        title="Assistant settings"
        subtitle="Configure access mode, knowledge documents, and evaluation runs for searchable chatbot methodologies."
      >
        <div className="grid gap-6">
          <section className="rounded-[1.75rem] bg-white/82 p-6 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
            <h2 className="text-xl font-semibold text-[var(--color-charcoal-blue)]">Access and active methodology</h2>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              Choose whether chat is publicly available and which methodology profile answers requests.
            </p>

            <form
              className="mt-5 grid gap-4 md:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault()
                settingsForm.transform((data) => ({ assistant: data }))
                settingsForm.patch(`${organizationBasePath}/settings/assistant`)
              }}
            >
              <div>
                <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Chat access mode</label>
                <Select
                  onValueChange={(value) => settingsForm.setData("chat_access_mode", value)}
                  value={settingsForm.data.chat_access_mode}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select access mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="authenticated_chat">Logged-in users only</SelectItem>
                    <SelectItem value="public_chat">Public access</SelectItem>
                  </SelectContent>
                </Select>
                {joinErrors(errors, "chat_access_mode") ? (
                  <p className="mt-2 text-sm text-[var(--color-danger)]">{joinErrors(errors, "chat_access_mode")}</p>
                ) : null}
              </div>

              <div>
                <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Active methodology</label>
                <Select
                  onValueChange={setActiveMethodology}
                  value={settingsForm.data.active_methodology_id}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select methodology" />
                  </SelectTrigger>
                  <SelectContent>
                    {methodologies.map((methodology) => (
                      <SelectItem key={methodology.id} value={methodology.id}>{methodologyLabel(methodology)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {joinErrors(errors, "active_methodology") ? (
                  <p className="mt-2 text-sm text-[var(--color-danger)]">{joinErrors(errors, "active_methodology")}</p>
                ) : null}
              </div>

              <div className="md:col-span-2">
                <Button disabled={settingsForm.processing} type="submit">Save assistant settings</Button>
              </div>
            </form>
          </section>

          <section className="rounded-[1.75rem] bg-white/82 p-6 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
            <h2 className="text-xl font-semibold text-[var(--color-charcoal-blue)]">Knowledgebase documents</h2>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">
              Upload PDFs or paste URLs. HTML pages, PDFs, and readable text documents are ingested asynchronously for retrieval.
            </p>

            <form
              className="mt-5 space-y-4"
              onSubmit={(event) => {
                event.preventDefault()
                uploadForm.transform((data) => ({ knowledge_documents: data }))
                uploadForm.post(`${organizationBasePath}/settings/knowledge-documents`, {
                  forceFormData: true,
                  onSuccess: () => uploadForm.reset("files", "urls")
                })
              }}
            >
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-[var(--color-charcoal-blue)]" htmlFor="knowledge-pdf-files">PDF files</label>
                  <input
                    accept="application/pdf"
                    className="input-field mt-2"
                    id="knowledge-pdf-files"
                    multiple
                    onChange={(event) => uploadForm.setData("files", Array.from(event.target.files || []))}
                    type="file"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[var(--color-charcoal-blue)]" htmlFor="knowledge-document-urls">URLs</label>
                  <textarea
                    className="input-field mt-2 min-h-28 resize-y"
                    id="knowledge-document-urls"
                    onChange={(event) => uploadForm.setData("urls", event.target.value)}
                    placeholder="https://example.com/report.pdf&#10;https://example.com/policy"
                    value={uploadForm.data.urls}
                  />
                </div>
              </div>

              <Button disabled={uploadForm.processing || (uploadForm.data.files.length === 0 && uploadForm.data.urls.trim().length === 0)} type="submit">
                Import documents
              </Button>
            </form>

            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-[var(--color-text-faint)]">
                  <tr>
                    <th className="px-2 py-2 font-semibold">Document</th>
                    <th className="px-2 py-2 font-semibold">Status</th>
                    <th className="px-2 py-2 font-semibold">Pages</th>
                    <th className="px-2 py-2 font-semibold">Chunks</th>
                    <th className="px-2 py-2 font-semibold">Datasets</th>
                    <th className="px-2 py-2 font-semibold">Indexed</th>
                    <th className="px-2 py-2 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {knowledge_documents.map((document) => (
                    <tr className="border-t border-[var(--color-border)]" key={document.id}>
                      <td className="px-2 py-2">
                        <p className="font-medium text-[var(--color-charcoal-blue)]">{document.title}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {document.file_url ? <a className="text-xs text-[var(--color-verdigris)]" href={document.file_url}>Open file</a> : null}
                          {document.source_url ? <a className="text-xs text-[var(--color-verdigris)]" href={document.source_url}>Open source</a> : null}
                        </div>
                        {document.failure_reason ? <p className="text-xs text-[var(--color-danger)]">{document.failure_reason}</p> : null}
                      </td>
                      <td className="px-2 py-2">{document.status}</td>
                      <td className="px-2 py-2">{document.pages_count}</td>
                      <td className="px-2 py-2">{document.chunks_count}</td>
                      <td className="px-2 py-2">{document.referenced_dataset_count}</td>
                      <td className="px-2 py-2">{formatDate(document.indexed_at)}</td>
                      <td className="px-2 py-2 text-right">
                        <Button
                          aria-label={`Delete ${document.title}`}
                          onClick={() => setDocumentToDelete(document)}
                          size="icon"
                          type="button"
                          variant="ghost"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <MethodologyProfilesSection
            activeMethodologyId={lockedActiveMethodologyId}
            archivedMethodologies={archived_methodologies}
            chatModelOptions={chat_model_options}
            embeddingModelOptions={embedding_model_options}
            methodologies={methodologies}
            onCreate={() => setCreateDialogOpen(true)}
            organizationBasePath={organizationBasePath}
          />

          <section className="rounded-[1.75rem] bg-white/82 p-6 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[var(--color-charcoal-blue)]">Evaluation harness</h2>
                <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                  Generate benchmark Q/A cases, then run one or more methodology profiles against the dataset.
                </p>
              </div>

              <Button asChild type="button" variant="outline">
                <Link href={`${organizationBasePath}/settings/benchmarks`}>Explore benchmarks</Link>
              </Button>
            </div>

            <div className="mt-5 grid gap-6 xl:grid-cols-2">
              <form
                className="rounded-[0.5rem] border border-[var(--color-border)] bg-[var(--color-panel)] p-4 space-y-4 shadow-[var(--shadow-soft)]"
                onSubmit={(event) => {
                  event.preventDefault()
                  datasetForm.transform((data) => ({ eval_dataset: data }))
                  datasetForm.post(`${organizationBasePath}/settings/eval-datasets`, {
                    onSuccess: () => {
                      datasetForm.reset("description", "generator_model", "case_limit")
                    }
                  })
                }}
              >
                <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">Generate dataset</p>

                <div>
                  <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Dataset name</label>
                  <input
                    className="input-field mt-2"
                    onChange={(event) => datasetForm.setData("name", event.target.value)}
                    value={datasetForm.data.name}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Generation instructions</label>
                  <textarea
                    className="input-field mt-2 min-h-28 resize-y"
                    onChange={(event) => datasetForm.setData("description", event.target.value)}
                    value={datasetForm.data.description}
                  />
                </div>

                <ModelField
                  id="dataset-generator-model"
                  label="Question generation model"
                  onChange={(value) => datasetForm.setData("generator_model", value)}
                  options={chat_model_options}
                  placeholder="Choose a question model"
                  value={datasetForm.data.generator_model}
                />

                <div>
                  <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Case limit</label>
                  <input
                    className="input-field mt-2"
                    min={1}
                    onChange={(event) => datasetForm.setData("case_limit", Number(event.target.value) || 1)}
                    type="number"
                    value={datasetForm.data.case_limit}
                  />
                </div>

                <Button disabled={datasetForm.processing} type="submit">Generate benchmark dataset</Button>
              </form>

              <form
                className="rounded-[0.5rem] border border-[var(--color-border)] bg-[var(--color-panel)] p-4 space-y-4 shadow-[var(--shadow-soft)]"
                onSubmit={(event) => {
                  event.preventDefault()
                  runForm.transform((data) => ({ eval_run: data }))
                  runForm.post(`${organizationBasePath}/settings/eval-runs`)
                }}
              >
                <p className="text-sm font-semibold text-[var(--color-charcoal-blue)]">Run evaluations</p>

                <div>
                  <label className="text-sm font-medium text-[var(--color-charcoal-blue)]">Dataset</label>
                  <Select
                    onValueChange={(value) => runForm.setData("eval_dataset_id", value)}
                    value={runForm.data.eval_dataset_id}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select dataset" />
                    </SelectTrigger>
                    <SelectContent>
                      {eval_datasets.map((dataset) => (
                        <SelectItem key={dataset.id} value={dataset.id}>{`${dataset.name} (${dataset.case_count} cases)`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ModelField
                  id="eval-judge-model"
                  label="Judge model"
                  onChange={(value) => runForm.setData("judge_model", value)}
                  options={chat_model_options}
                  placeholder="Choose a judge model"
                  value={runForm.data.judge_model}
                />

                <ModelField
                  id="eval-question-model"
                  label="Question model for run metadata"
                  onChange={(value) => runForm.setData("question_generation_model", value)}
                  options={chat_model_options}
                  placeholder="Choose a question model"
                  value={runForm.data.question_generation_model}
                />

                <div>
                  <p className="text-sm font-medium text-[var(--color-charcoal-blue)]">Methodologies to run</p>
                  <div className="mt-2 space-y-2">
                    {methodologies.map((methodology) => (
                      <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]" key={methodology.id}>
                        <input
                          checked={(runForm.data.methodology_ids || []).includes(methodology.id)}
                          onChange={(event) => toggleMethodologySelection(methodology.id, event.target.checked)}
                          type="checkbox"
                        />
                        <span>{methodology.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <Button disabled={runForm.processing || !runForm.data.eval_dataset_id} type="submit">Queue evaluation runs</Button>
              </form>
            </div>

            <div className="mt-6 grid gap-6 xl:grid-cols-2">
              <div className="overflow-x-auto">
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--color-text-faint)]">Datasets</h3>
                <table className="mt-3 min-w-full text-left text-sm">
                  <thead className="text-[var(--color-text-faint)]">
                    <tr>
                      <th className="px-2 py-2 font-semibold">Name</th>
                      <th className="px-2 py-2 font-semibold">Status</th>
                      <th className="px-2 py-2 font-semibold">Cases</th>
                      <th className="px-2 py-2 font-semibold">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eval_datasets.map((dataset) => (
                      <tr className="border-t border-[var(--color-border)]" key={dataset.id}>
                        <td className="px-2 py-2">
                          <Link
                            className="font-medium text-[var(--color-charcoal-blue)] underline decoration-[rgb(42_157_143/0.35)] underline-offset-4 hover:text-[var(--color-verdigris)]"
                            href={`${organizationBasePath}/settings/benchmarks?dataset_id=${dataset.id}`}
                          >
                            {dataset.name}
                          </Link>
                        </td>
                        <td className="px-2 py-2">{dataset.status}</td>
                        <td className="px-2 py-2">{dataset.case_count}</td>
                        <td className="px-2 py-2">{formatDate(dataset.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="overflow-x-auto">
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--color-text-faint)]">Recent runs</h3>
                <table className="mt-3 min-w-full text-left text-sm">
                  <thead className="text-[var(--color-text-faint)]">
                    <tr>
                      <th className="px-2 py-2 font-semibold">Methodology</th>
                      <th className="px-2 py-2 font-semibold">Dataset</th>
                      <th className="px-2 py-2 font-semibold">Status</th>
                      <th className="px-2 py-2 font-semibold">Avg score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eval_runs.map((run) => (
                      <tr className="border-t border-[var(--color-border)]" key={run.id}>
                        <td className="px-2 py-2">
                          <Link
                            className="font-medium text-[var(--color-charcoal-blue)] underline decoration-[rgb(42_157_143/0.35)] underline-offset-4 hover:text-[var(--color-verdigris)]"
                            href={run.detail_url || `${organizationBasePath}/settings/eval-runs/${run.id}`}
                          >
                            {run.methodology_label}
                          </Link>
                        </td>
                        <td className="px-2 py-2">
                          <Link
                            className="text-[var(--color-charcoal-blue)] underline decoration-[rgb(42_157_143/0.35)] underline-offset-4 hover:text-[var(--color-verdigris)]"
                            href={`${organizationBasePath}/settings/benchmarks?dataset_id=${run.dataset_id}`}
                          >
                            {run.dataset_name}
                          </Link>
                        </td>
                        <td className="px-2 py-2">{run.status}</td>
                        <td className="px-2 py-2">{run.average_score ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </AppShell>

      <MethodologyDialog
        chatModelOptions={chat_model_options}
        embeddingModelOptions={embedding_model_options}
        onOpenChange={setCreateDialogOpen}
        open={createDialogOpen}
        organizationBasePath={organizationBasePath}
      />

      <ConfirmDialog
        confirmLabel="Delete PDF"
        description={
          documentToDelete?.referenced_dataset_count > 0
            ? `This will permanently delete the PDF, indexed chunks, embeddings, QMD export, and ${documentToDelete.referenced_dataset_count} benchmark dataset${documentToDelete.referenced_dataset_count === 1 ? "" : "s"} that reference it.`
            : "This will permanently delete the PDF, indexed chunks, embeddings, and QMD export."
        }
        onConfirm={() => {
          if (!documentToDelete) return

          documentDeleteForm.delete(`${organizationBasePath}/settings/knowledge-documents/${documentToDelete.id}`, {
            preserveScroll: true,
            onSuccess: () => setDocumentToDelete(null)
          })
        }}
        onOpenChange={(open) => {
          if (!open) setDocumentToDelete(null)
        }}
        open={Boolean(documentToDelete)}
        processing={documentDeleteForm.processing}
        title={`Delete ${documentToDelete?.title || "PDF"}?`}
      />
    </>
  )
}
